import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import BusinessVerification from "@/pages/BusinessVerification";
import Dashboard from "@/pages/Dashboard";
import SolutionsBrowse from "@/pages/SolutionsBrowse";
import SolutionDetails from "@/pages/SolutionDetails";
import PriceComparison from "@/pages/PriceComparison";
import TCOAnalysis from "@/pages/TCOAnalysis";
import AdminPanel from "@/pages/AdminPanel";
import ConfigureSolution from "@/pages/ConfigureSolution";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = async () => {
    // Skip auth check if we have a session_id in hash - let AuthCallback handle it
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      const userData = response.data.user;
      const profileData = response.data.business_profile;
      setUser(userData);
      setBusinessProfile(profileData);
      setOrganization(response.data.organization);
      setOrgMembers(response.data.org_members || []);
      setIsAuthenticated(true);
      // Identify user in PostHog for session attribution
      if (userData && typeof window !== 'undefined' && window.posthog) {
        window.posthog.identify(userData.user_id, {
          email: userData.email,
          name: userData.name,
          is_admin: userData.is_admin
        });
        if (profileData?.company_name) {
          window.posthog.group('company', profileData.company_name, {
            company_size: profileData.company_size,
            industry: profileData.industry
          });
        }
      }
    } catch (error) {
      setUser(null);
      setBusinessProfile(null);
      setOrganization(null);
      setOrgMembers([]);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data.user);
      setBusinessProfile(response.data.business_profile);
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setBusinessProfile(null);
      setIsAuthenticated(false);
    }
  };

  const updateBusinessProfile = (profile) => {
    setBusinessProfile(profile);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        businessProfile,
        organization,
        orgMembers,
        loading,
        isAuthenticated,
        login,
        logout,
        checkAuth,
        updateBusinessProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireBusinessProfile = false }) => {
  const { isAuthenticated, businessProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireBusinessProfile && !businessProfile) {
    return <Navigate to="/verify-business" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const { loading } = useAuth();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render - this must happen BEFORE any auth checks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Don't render routes until auth state is determined (prevents flash redirects)
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      
      {/* Business verification - requires auth but not business profile */}
      <Route
        path="/verify-business"
        element={
          <ProtectedRoute>
            <BusinessVerification />
          </ProtectedRoute>
        }
      />
      
      {/* Protected routes - require auth and business profile */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireBusinessProfile>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/solutions"
        element={
          <ProtectedRoute requireBusinessProfile>
            <SolutionsBrowse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/solutions/:solutionId"
        element={
          <ProtectedRoute requireBusinessProfile>
            <SolutionDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bundles/:bundleId"
        element={
          <ProtectedRoute requireBusinessProfile>
            <SolutionDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare"
        element={
          <ProtectedRoute requireBusinessProfile>
            <PriceComparison />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configure"
        element={
          <ProtectedRoute requireBusinessProfile>
            <ConfigureSolution />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tco"
        element={
          <ProtectedRoute requireBusinessProfile>
            <TCOAnalysis />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireBusinessProfile>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
